import React, { useCallback, useState } from 'react';
import { useResizeObserver } from '@wojtekmaj/react-hooks';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { logError } from '../../logging/logSentryError';
import { InlineLink } from '../ReusableStyledComponents/InlineLink';
import { VerticalSpacing } from '../VerticalSpacing';
import { DocumentWrapper, PageSeparator } from './PDFViewer.styles';
import { V3Loader } from '../Loader';
import { V3ParagraphGrey35 } from '../ReusableStyledComponents/V3ParagraphGrey35';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.js',
    import.meta.url,
).toString();

const MAX_PAGES = 30;

type PDFViewerProps = {
    url: string
}

const resizeObserverOptions = {};
const maxWidth = 736;

export const PDFViewer = ({ url }: PDFViewerProps) => {
    const [numPages, setNumPages] = useState(0);
    const [maxPagesReached, setMaxPagesReached] = useState(false);
    const [pagesRendered, setPagesRendered] = useState(false);
    const [scale] = useState(1);

    function onDocumentLoadSuccess({ numPages }: any) {
        setNumPages(numPages);
    }

    const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>();

    const onResize = useCallback<ResizeObserverCallback>((entries) => {
        const [entry] = entries;

        if (entry) {
            setContainerWidth(entry.contentRect.width);
        }
    }, []);

    useResizeObserver(containerRef, resizeObserverOptions, onResize);

    return (
        <>
            <div>
                <DocumentWrapper ref={setContainerRef}>
                    <Document
                        file={url}
                        onLoadSuccess={onDocumentLoadSuccess}
                        loading={<V3Loader />}
                        onLoadError={(error) => {
                            if (error?.message && error?.message !== "Worker was destroyed") {
                                logError(`Error loading PDF ${error.message} ${url}`);
                            }
                        }}
                        error={
                            <V3ParagraphGrey35>
                                PDF rendering failed. Could you check if you are using the most recent version of your browser, and if not, update it?
                                If the error persists, please let us know at <InlineLink href='mailto:hello@authory.com?subject=PDF'>hello@authory.com</InlineLink>
                            </V3ParagraphGrey35>
                        }
                    >
                        {
                            Array.from(
                                new Array(Math.min(numPages, MAX_PAGES)),
                                (_, index) => (
                                    <React.Fragment key={index + 1}>
                                        <Page
                                            renderTextLayer={false}
                                            loading={""}
                                            pageNumber={index + 1}
                                            scale={scale}
                                            width={containerWidth ? Math.min(containerWidth, maxWidth) : maxWidth}
                                            onLoadSuccess={({ _pageIndex }: { _pageIndex: number }) => {
                                                if (_pageIndex === MAX_PAGES - 1) setMaxPagesReached(true);
                                                if (_pageIndex === numPages - 1) setPagesRendered(true);
                                            }}
                                        />
                                        {
                                            (pagesRendered || maxPagesReached) && <PageSeparator />
                                        }
                                    </React.Fragment>
                                ),
                            )
                        }
                    </Document>
                </DocumentWrapper>
            </div >
            {
                maxPagesReached && <VerticalSpacing top={32}>
                    <V3ParagraphGrey35>This PDF is more than {MAX_PAGES} pages long. To see the remaining pages, please <InlineLink href={url} target="_blank">download the PDF</InlineLink>.</V3ParagraphGrey35>
                </ VerticalSpacing >
            }
        </>
    );

}
export default PDFViewer;